import type { PostHogConfig } from 'posthog-js';
import { getRuntimeEnvironment } from '@planner/config';
import { appVersion, brand, region } from '../env.js';
import { serializeEvent } from './payload.js';

export const analyticsEnvironment = getRuntimeEnvironment(
  import.meta.env.VITE_ENVIRONMENT ?? 'local',
);
export const superProperties = {
  environment: analyticsEnvironment,
  region: region.code,
  brand: brand.id,
  app_version: appVersion,
};
const paths = new Set([
  '/',
  '/planner/type',
  '/planner/measure',
  '/planner/lock',
  '/planner/colour',
  '/planner/review',
  '/cart',
  '/privacy-policy',
  '/terms-and-conditions',
  '/warranty',
  '/contact-us',
]);
export function safePath(value: string): string {
  try {
    const path = new URL(value, window.location.origin).pathname;
    return paths.has(path) ? path : '/other';
  } catch {
    return '/other';
  }
}

// Keep SDK routing/session metadata, not arbitrary attribution, DOM or person properties.
const allowed = new Set([
  'token',
  'distinct_id',
  '$device_id',
  '$session_id',
  '$window_id',
  '$lib',
  '$lib_version',
  '$insert_id',
  '$time',
  '$sent_at',
  '$event_type',
  '$snapshot_data',
  '$snapshot_bytes',
  '$is_identified',
  '$process_person_profile',
  '$recording_status',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$pageview_id',
  '$prev_pageview_id',
  '$prev_pageview_pathname',
  '$prev_pageview_duration',
  '$prev_pageview_max_scroll_percentage',
  '$prev_pageview_max_content_percentage',
  '$prev_pageview_last_scroll_percentage',
  '$prev_pageview_last_content_percentage',
  'environment',
  'region',
  'brand',
  'app_version',
]);

export const beforeSend: NonNullable<PostHogConfig['before_send']> = (event) => {
  if (!event) return event;
  const properties = event.properties;
  const payload = event.event.startsWith('$') ? {} : serializeEvent(event.event, properties);
  if (!payload) {
    console.warn('Analytics: invalid event payload was dropped.');
    return null;
  }
  const path = safePath(String(properties.$current_url ?? window.location.href));
  for (const key of Object.keys(properties)) {
    if (!allowed.has(key)) delete properties[key];
  }
  if (typeof properties.$prev_pageview_pathname === 'string') {
    properties.$prev_pageview_pathname = safePath(properties.$prev_pageview_pathname);
  }
  properties.$current_url = `${window.location.origin}${path}`;
  properties.$pathname = path;
  Object.assign(properties, payload, superProperties);
  return event;
};
